import React from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import { type HighlightedText as HighlightedTextType, ContentSnippet, extractTextFromHTML } from '@HELPERS/textHighlighting';

import styles from './ContentMatch.module.scss';

const cx: CX = classnames.bind(styles);

function HighlightedText({ parts }: { parts: HighlightedTextType[] }) {
    return (
        <>
            {parts.map((part) => (
                <span
                    key={`highlighted-${part.text}-${part.isHighlighted}`}
                    className={cx({ highlighted: part.isHighlighted })}
                >
                    {part.text}
                </span>
            ))}
        </>
    );
}

function ContentMatch(props: Props) {
    const {
        title,
        type,
        snippets,
        onClick,
        metadata,
    } = props;

    // Clean HTML from title to ensure no HTML tags are displayed
    const cleanTitle = extractTextFromHTML(title);

    function getTypeIcon() {
        switch (type) {
            case 'document':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                    </svg>
                );
            case 'chapter':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                        <line x1="10" y1="8" x2="16" y2="8" />
                        <line x1="10" y1="12" x2="14" y2="12" />
                    </svg>
                );
            case 'section':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="9" y1="15" x2="15" y2="15" />
                        <line x1="9" y1="18" x2="12" y2="18" />
                    </svg>
                );
            case 'variant':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                        <path d="M12 11h4" />
                        <path d="M12 16h4" />
                        <path d="M8 11h.01" />
                        <path d="M8 16h.01" />
                    </svg>
                );
            default:
                return null;
        }
    }

    function getTypeLabel() {
        switch (type) {
            case 'document':
                return 'Document';
            case 'chapter':
                return 'Chapter';
            case 'section':
                return 'Section';
            case 'variant':
                return 'Variant';
            default:
                return '';
        }
    }

    return (
        <div className={cx('content-match')}>
            <button
                type="button"
                className={cx('content-match-header')}
                onClick={onClick}
            >
                <div className={cx('header-content')}>
                    <div className={cx('icon-and-type')}>
                        <div className={cx('type-icon', type)}>
                            {getTypeIcon()}
                        </div>
                        <span className={cx('type-label')}>
                            {getTypeLabel()}
                        </span>
                    </div>
                    
                    <div className={cx('title')}>
                        {cleanTitle}
                    </div>
                    
                    {metadata && (
                        <div className={cx('metadata')}>
                            {metadata}
                        </div>
                    )}
                </div>
                
                <div className={cx('arrow')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9,18 15,12 9,6" />
                    </svg>
                </div>
            </button>
            
            <div className={cx('snippets')}>
                {snippets.map((snippet) => (
                    <div key={`snippet-${snippet.snippet.slice(0, 50)}-${snippet.hasMoreBefore}-${snippet.hasMoreAfter}`} className={cx('snippet')}>
                        <div className={cx('snippet-text')}>
                            {snippet.hasMoreBefore && (
                                <span className={cx('ellipsis')}>...</span>
                            )}
                            <HighlightedText parts={snippet.highlightedSnippet} />
                            {snippet.hasMoreAfter && (
                                <span className={cx('ellipsis')}>...</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

ContentMatch.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['document', 'chapter', 'section', 'variant']).isRequired,
    snippets: PropTypes.arrayOf(PropTypes.shape({
        snippet: PropTypes.string.isRequired,
        highlightedSnippet: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            isHighlighted: PropTypes.bool.isRequired,
        })).isRequired,
        hasMoreBefore: PropTypes.bool.isRequired,
        hasMoreAfter: PropTypes.bool.isRequired,
    })).isRequired,
    onClick: PropTypes.func.isRequired,
    metadata: PropTypes.string,
};

ContentMatch.defaultProps = {
    metadata: undefined,
};

type Props = {
    title: string;
    type: 'document' | 'chapter' | 'section' | 'variant';
    snippets: ContentSnippet[];
    onClick: () => void;
    metadata?: string;
};

export default ContentMatch; 